import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { formatDecimalHours } from "../../utils/timeUtils";
import EditAttendanceDialog from "../../components/EditAttendanceDialog";

export default function AdminStaffAttendanceCalendar() {
  const { staffId, month, year } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(location.state || null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [staffType, setStaffType] = useState("Task-Based");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [boutiqueSchedule, setBoutiqueSchedule] = useState([]);

  useEffect(() => {
    fetchMonthAttendance();
    fetchBoutiqueSchedule();
  }, [staffId, month, year]);

  const fetchBoutiqueSchedule = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/schedule/${year}/${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setBoutiqueSchedule(data.dates || []);
      }
    } catch (error) {
      console.error("Error fetching boutique schedule:", error);
    }
  };

  const fetchMonthAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // If staff info not available, fetch it
      if (!staffInfo) {
        const staffResponse = await fetch(`${API_BASE_URL}/api/staff`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (staffResponse.ok) {
          const allStaff = await staffResponse.json();
          const staff = allStaff.find(s => s.StaffId === parseInt(staffId));
          if (staff) {
            setStaffInfo(staff);
            setStaffType(staff.StaffType || "Task-Based");
          }
        }
      } else {
        setStaffType(staffInfo.StaffType || "Task-Based");
      }

      // Fetch ALL attendance records for this staff member
      const response = await fetch(
        `${API_BASE_URL}/api/staff/attendance/${staffId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const allRecords = await response.json();
        
        // Filter records for the specific month and year
        const monthRecords = allRecords.filter(record => {
          const recordDate = new Date(record.Date);
          return recordDate.getMonth() === parseInt(month) - 1 && 
                 recordDate.getFullYear() === parseInt(year);
        });
        
        setAttendanceRecords(monthRecords);

        // Create a map for quick lookup
        const map = {};
        monthRecords.forEach((record) => {
          const recordDate = new Date(record.Date);
          const day = recordDate.getDate();
          map[day] = {
            status: record.Status,
            reachingTime: record.PunchInTime, // Use PunchInTime as reaching time
            punchInTime: record.PunchInTime,
            punchOutTime: record.PunchOutTime,
            totalWorkHours: record.TotalWorkHours,
            date: record.Date,
            AttendanceId: record.AttendanceId,
            ...record
          };
        });
        setAttendanceMap(map);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };

  const getFirstDayOfMonth = () => {
    return new Date(parseInt(year), parseInt(month) - 1, 1).getDay();
  };

  const getMonthName = () => {
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Present":
        return "#4CAF50";
      case "Absent":
        return "#F44336";
      case "Late":
        return "#FF9800";
      default:
        return "#9E9E9E";
    }
  };

  const getStatusLightColor = (status) => {
    switch (status) {
      case "Present":
        return "#E8F5E9";
      case "Absent":
        return "#FFEBEE";
      case "Late":
        return "#FFF3E0";
      default:
        return "#F5F5F5";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "Present":
        return "P";
      case "Absent":
        return "A";
      case "Late":
        return "L";
      default:
        return "";
    }
  };

  const getBoutiqueDayType = (day) => {
    const date = new Date(parseInt(year), parseInt(month) - 1, day);
    const dateStr = date.toISOString().split('T')[0];
    const scheduleEntry = boutiqueSchedule.find(s => s.Date.split('T')[0] === dateStr);
    
    if (scheduleEntry) {
      return {
        type: scheduleEntry.DayType,
        reason: scheduleEntry.Reason
      };
    }
    
    // Check if it's a default Sunday
    if (date.getDay() === 0) {
      return { type: 'DefaultSunday', reason: 'Default holiday' };
    }
    
    return { type: 'WorkingDay', reason: null };
  };

  const formatTimeOnly = (timeString) => {
    if (!timeString) return "";
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString.substring(0, 5);
    }
    try {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
    } catch (e) {
      console.error('Error formatting time:', e);
    }
    return timeString;
  };

  const handleDayClick = async (day) => {
    const attendance = attendanceMap[day];
    const dateStr = `${year}-${month.padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    
    if (attendance) {
      // Fetch full attendance details including breaks for this specific date
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_BASE_URL}/api/staff/attendance-by-date?date=${dateStr}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          const dayData = await response.json();
          // Find this staff member's data
          const staffDayData = dayData.find(d => d.StaffId === parseInt(staffId));
          
          // console.log('Staff day data:', staffDayData);
          // console.log('Attendance from map:', attendance);
          
          if (staffDayData) {
            // Edit existing attendance with breaks data
            // Ensure AttendanceId is included
            setSelectedAttendance({
              ...staffDayData,
              AttendanceId: staffDayData.AttendanceId || attendance.AttendanceId,
              StaffId: parseInt(staffId),
              Date: dateStr,
              FullName: staffInfo?.FullName,
            });
          } else {
            // Fallback to attendance map data
            setSelectedAttendance({
              ...attendance,
              StaffId: parseInt(staffId),
              Date: dateStr,
              FullName: staffInfo?.FullName,
              Breaks: [],
            });
          }
        } else {
          // Fallback to attendance map data
          setSelectedAttendance({
            ...attendance,
            StaffId: parseInt(staffId),
            Date: dateStr,
            FullName: staffInfo?.FullName,
            Breaks: [],
          });
        }
      } catch (error) {
        console.error("Error fetching day details:", error);
        // Fallback to attendance map data
        setSelectedAttendance({
          ...attendance,
          StaffId: parseInt(staffId),
          Date: dateStr,
          FullName: staffInfo?.FullName,
          Breaks: [],
        });
      }
    } else {
      // Create new attendance record
      setSelectedAttendance({
        StaffId: parseInt(staffId),
        Date: dateStr,
        Status: "Absent",
        PunchInTime: null,
        PunchOutTime: null,
        Breaks: [],
        FullName: staffInfo?.FullName,
      });
    }
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedAttendance(null);
  };

  const handleEditSave = () => {
    fetchMonthAttendance();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            border: "1px solid #E0E0E0",
            bgcolor: "#FAFAFA",
            borderRadius: { xs: 1, sm: 1.5 },
            minHeight: "inherit",
            maxHeight: "inherit",
          }}
        />
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const attendance = attendanceMap[day];
      const hasAttendance = !!attendance;

      // Check boutique schedule for this day
      const boutiqueDayInfo = getBoutiqueDayType(day);
      const isHoliday = boutiqueDayInfo.type === 'Holiday' || boutiqueDayInfo.type === 'DefaultSunday';
      const isOpenSunday = boutiqueDayInfo.type === 'OpenSunday';
      
      // Determine background color
      let bgColor = "#F5F5F5"; // Default working day
      if (isHoliday) {
        bgColor = "#FFE5E5"; // Holiday color
      } else if (isOpenSunday) {
        bgColor = "#FFF9C4"; // Opened Sunday color
      } else if (hasAttendance) {
        bgColor = getStatusLightColor(attendance.status);
      }

      days.push(
        <Box
          key={day}
          onClick={() => !isHoliday && handleDayClick(day)}
          sx={{
            border: "1px solid #E0E0E0",
            bgcolor: bgColor,
            borderRadius: { xs: 1, sm: 1.5 },
            display: "flex",
            flexDirection: "column",
            position: "relative",
            minHeight: "inherit",
            maxHeight: "inherit",
            p: { xs: 0.25, sm: 0.5, md: 0.75 },
            transition: "all 0.2s",
            cursor: isHoliday ? "not-allowed" : "pointer",
            opacity: isHoliday ? 0.6 : 1,
            "&:hover": {
              opacity: isHoliday ? 0.6 : 0.9,
              transform: isHoliday ? "none" : "scale(1.02)",
              boxShadow: isHoliday ? 0 : 2,
            },
          }}
        >
          {/* Day Number */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: { xs: 2, sm: 4, md: 6 },
              left: { xs: 2, sm: 4, md: 6 },
              fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.875rem" },
              fontWeight: 600,
              color: "#666",
              zIndex: 1,
            }}
          >
            {day}
          </Typography>

          {/* Content Container */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: { xs: 1.5, sm: 2, md: 2.5 },
              gap: { xs: 0.25, sm: 0.5 },
            }}
          >
            {isHoliday && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#D32F2F",
                    fontWeight: 700,
                    fontSize: { xs: "0.45rem", sm: "0.55rem", md: "0.65rem" },
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  {boutiqueDayInfo.type === 'Holiday' ? 'HOLIDAY' : 'SUNDAY'}
                </Typography>
                {boutiqueDayInfo.reason && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#D32F2F",
                      fontWeight: 500,
                      fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                      textAlign: "center",
                      lineHeight: 1,
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {boutiqueDayInfo.reason}
                  </Typography>
                )}
              </>
            )}

            {isOpenSunday && !hasAttendance && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#f57c00",
                    fontWeight: 700,
                    fontSize: { xs: "0.45rem", sm: "0.55rem", md: "0.65rem" },
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  OPEN
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#f57c00",
                    fontWeight: 500,
                    fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                    lineHeight: 1,
                  }}
                >
                  Optional
                </Typography>
              </>
            )}

            {!isHoliday && hasAttendance && (
              <>
                {/* Status Badge */}
                <Box
                  sx={{
                    width: { xs: 16, sm: 24, md: 32, lg: 36 },
                    height: { xs: 16, sm: 24, md: 32, lg: 36 },
                    borderRadius: { xs: 1, sm: 1.5 },
                    bgcolor: getStatusColor(attendance.status),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 0.25, sm: 0.5 },
                  }}
                >
                  <Typography
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: { xs: "0.6rem", sm: "0.8rem", md: "1rem", lg: "1.1rem" },
                      lineHeight: 1,
                    }}
                  >
                    {getStatusLabel(attendance.status)}
                  </Typography>
                </Box>

                {/* Time Information */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: { xs: 0.1, sm: 0.2 },
                    width: "100%",
                  }}
                >
                  {staffType === "Hourly" && (
                    <>
                      {attendance.punchInTime && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#4CAF50",
                            fontSize: { xs: "0.3rem", sm: "0.4rem", md: "0.5rem" },
                            fontWeight: 600,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          In: {formatTimeOnly(attendance.punchInTime)}
                        </Typography>
                      )}
                      {attendance.punchOutTime && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#F44336",
                            fontSize: { xs: "0.3rem", sm: "0.4rem", md: "0.5rem" },
                            fontWeight: 600,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          Out: {formatTimeOnly(attendance.punchOutTime)}
                        </Typography>
                      )}
                      {attendance.totalWorkHours > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#1976D2",
                            fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                            fontWeight: 700,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          {formatDecimalHours(attendance.totalWorkHours)}
                        </Typography>
                      )}
                    </>
                  )}

                  {staffType === "Task-Based" && attendance.reachingTime && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                        fontWeight: 600,
                        lineHeight: 1,
                        textAlign: "center",
                      }}
                    >
                      {formatTimeOnly(attendance.reachingTime)}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      );
    }

    return days;
  };

  const calculateStats = () => {
    const present = attendanceRecords.filter((r) => r.Status === "Present").length;
    const absent = attendanceRecords.filter((r) => r.Status === "Absent").length;
    const late = attendanceRecords.filter((r) => r.Status === "Late").length;
    
    // Calculate working days in the selected month (excluding Sundays and boutique holidays)
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    let workingDays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const boutiqueDayInfo = getBoutiqueDayType(day);
      // Count as working day if it's not a holiday or default Sunday
      if (boutiqueDayInfo.type !== 'Holiday' && boutiqueDayInfo.type !== 'DefaultSunday') {
        workingDays++;
      }
    }
    
    return { present, absent, late, total: workingDays };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 }, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 2, md: 3 }, flexWrap: "wrap", gap: 1 }}>
          <IconButton
            onClick={() => navigate(`/admin/staff/${staffId}/attendance-salary`, { state: staffInfo })}
            sx={{
              mr: 2,
              bgcolor: "#8B7355",
              color: "white",
              "&:hover": { bgcolor: "#6D5A43" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              color: "#8B7355",
              fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            }}
          >
            {getMonthName()} - Attendance Calendar
          </Typography>
        </Box>

        {/* Stats Summary */}
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid item xs={6} sm={4}>
            <Card sx={{ bgcolor: "#E3F2FD", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1976D2" }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card sx={{ bgcolor: "#E8F5E9", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#4CAF50" }}>
                  {stats.present}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Present
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card sx={{ bgcolor: "#FFEBEE", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#F44336" }}>
                  {stats.absent}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Absent
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: "wrap" }}>
          <Chip
            label="P - Present"
            sx={{ bgcolor: "#4CAF50", color: "white", fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
          />
          <Chip
            label="A - Absent"
            sx={{ bgcolor: "#F44336", color: "white", fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
          />
        </Box>

        {/* Calendar */}
        <Card sx={{ boxShadow: 3, overflow: 'hidden', width: '100%' }}>
          <CardContent sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>
            {/* Day Headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: { xs: 0.5, sm: 1 },
                mb: { xs: 0.5, sm: 1 },
              }}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"].map((day) => (
                <Box
                  key={day}
                  sx={{
                    textAlign: "center",
                    py: { xs: 0.5, sm: 0.75, md: 1 },
                    bgcolor: "#BC965E",
                    borderRadius: { xs: 1, sm: 1.5 },
                    minHeight: { xs: 24, sm: 32, md: 40 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ 
                      fontWeight: 700, 
                      color: "#ffffff", 
                      fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.875rem" },
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: { xs: 0.5, sm: 1 },
                "& > *": {
                  minHeight: { xs: 80, sm: 100, md: 130, lg: 150 },
                  maxHeight: { xs: 100, sm: 120, md: 150, lg: 180 },
                }
              }}
            >
              {renderCalendar()}
            </Box>
          </CardContent>
        </Card>

        {/* Edit Attendance Dialog */}
        {selectedAttendance && (
          <EditAttendanceDialog
            open={editDialogOpen}
            onClose={handleEditClose}
            staffData={selectedAttendance}
            selectedDate={selectedAttendance.Date}
            onSave={handleEditSave}
          />
        )}
      </Box>
    </AppLayout>
  );
}